import { RadioCard } from "components/common/RadioCard";
import { RepaymentDetails } from "components/common/RepaymentDetails";
import {
  findRepaymentOption,
  RepaymentOption,
  RepaymentOptionType,
} from "domain/indicativeRepaymentOptions";
import { SelectedTreatmentsDetails } from "domain/selectedTreatments";
import { useCallback } from "react";

import {
  CRH_EXTEND_TERM,
  CRH_OPTION_HEADING,
  CRH_OPTIONS_HEADING,
  CRH_WITHOUT_EXTEND_TERM,
} from "./RequestRepaymentHoliday.const";

type ExtendTermCRHProps = {
  extendTerm: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

const ExtendTermCRH = ({
  extendTerm,
  selectedTreatments,
  onChange,
}: ExtendTermCRHProps) => {
  const isSelected =
    extendTerm.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const handleChange = useCallback(
    () =>
      onChange({
        ...selectedTreatments,
        selectedOptionType: extendTerm.optionType,
        repaymentOption: extendTerm,
      }),
    [selectedTreatments, onChange, extendTerm]
  );
  return (
    <>
      <RadioCard
        name="crhOptions"
        isSelected={isSelected}
        value={extendTerm.optionType}
        onChange={handleChange}
        label={CRH_EXTEND_TERM}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={extendTerm}
          />
        )}
      </RadioCard>
    </>
  );
};
type JustCRHProps = {
  justCRH: RepaymentOption;
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

const JustCRH = ({ justCRH, selectedTreatments, onChange }: JustCRHProps) => {
  const isSelected =
    justCRH.optionType === selectedTreatments.selectedOptionType;
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const handleChange = useCallback(
    () =>
      onChange({
        ...selectedTreatments,
        selectedOptionType: justCRH.optionType,
        repaymentOption: justCRH,
      }),
    [selectedTreatments, onChange, justCRH]
  );
  return (
    <>
      <RadioCard
        name="crhOptions"
        isSelected={isSelected}
        value={justCRH.optionType}
        onChange={handleChange}
        label={CRH_WITHOUT_EXTEND_TERM}
      >
        {existingLoanDetails && (
          <RepaymentDetails
            existingLoanDetails={existingLoanDetails}
            repaymentOption={justCRH}
          />
        )}
      </RadioCard>
    </>
  );
};
type CRHOptionsProps = {
  repaymentOptions: RepaymentOption[];
  selectedTreatments: SelectedTreatmentsDetails;
  onChange: (selectedTreatments: SelectedTreatmentsDetails) => void;
};

export const CRHOptions = ({
  repaymentOptions,
  selectedTreatments,
  onChange,
}: CRHOptionsProps) => {
  const getRepaymentOption = findRepaymentOption(repaymentOptions);
  const extendTermCRH = getRepaymentOption(RepaymentOptionType.CRHExtendTerm);
  const justCRH = getRepaymentOption(RepaymentOptionType.CRHWithoutExtendTerm);
  const existingLoanDetails = selectedTreatments.existingLoanDetails;
  const ONE_OPTION_FLAG =
    existingLoanDetails != null
      ? existingLoanDetails.numberOfRepayments < 7
      : false;

  return (
    <div className="selected-options section-margin">
      <label id="crh-repaymentOptions" className="title">
        {ONE_OPTION_FLAG ? CRH_OPTION_HEADING : CRH_OPTIONS_HEADING}
      </label>
      <fieldset aria-labelledby="crh-repaymentOptions">
        {!!extendTermCRH && (
          <ExtendTermCRH
            extendTerm={extendTermCRH}
            selectedTreatments={selectedTreatments}
            onChange={onChange}
          />
        )}
        {!!justCRH && (
          <JustCRH
            justCRH={justCRH}
            selectedTreatments={selectedTreatments}
            onChange={onChange}
          />
        )}
      </fieldset>
    </div>
  );
};
